import { Fragment } from "react";

import Box from "@/components/Box";
import Divider from "@/components/Divider";
import { InternalTextLink } from "@/components/Links";
import Loading from "@/components/Loading";
import OrderStatusBadges from "@/components/OrderStatusBadges";
import { ORDER_RETEST_STATUS } from "@/core/constants";
import { CMP_ORDER_URL, getCmpUrl } from "@/core/urls";
import Group from "@/tpo/Group";
import LinkWrapper from "@/tpo/LinkWrapper";
import Stack from "@/tpo/Stack";

import getProgressStatusBadgeProps from "../utils/getProgressStatusBadgeProps";
import DashboardCircleArrow from "./DashboardCircleArrow";
import {
  cmpHomeDashboardEmptyMessage,
  cmpHomeDashboardListInnerStackGap,
  cmpHomeDashboardListRowPy,
  cmpHomeDashboardListSecondary,
  cmpHomeDashboardOrderDateOrderGap,
  cmpHomeDashboardOrderMetaBold,
  cmpHomeDashboardOrderMetaLink,
  cmpHomeDashboardSectionRowGap
} from "./dashboardLayout";

function formatOrderDate(checkoutDate) {
  if (!checkoutDate) {
    return null;
  }
  return new Date(checkoutDate).toLocaleDateString();
}

export default function LatestOrdersList({
  orders,
  loading,
  organisationId,
  emptyMessage = "No orders available"
}) {
  if (loading && !orders?.length) {
    return <Loading />;
  }

  if (!orders?.length) {
    return <Box {...cmpHomeDashboardEmptyMessage}>{emptyMessage}</Box>;
  }

  return (
    <>
      {orders.map((order, index) => {
        const orderUrl = getCmpUrl(CMP_ORDER_URL, {
          organisationId,
          orderId: order.id
        });

        const statusBadges = (
          <OrderStatusBadges
            order={order}
            showPaymentStatus={order.status !== ORDER_RETEST_STATUS}
            gap={2}
            alignSelf="center"
            badgeProps={getProgressStatusBadgeProps({
              organisationId,
              patientId: order.userId,
              derivedProgressStatus: order.derivedProgressStatus
            })}
          />
        );

        const orderLinkButton = (
          <Box flexShrink={0} display="flex" alignItems="center" as={LinkWrapper} to={orderUrl}>
            <DashboardCircleArrow linked={false} />
          </Box>
        );

        return (
          <Fragment key={order.id}>
            <Stack
              width="100%"
              gap={cmpHomeDashboardListInnerStackGap}
              py={cmpHomeDashboardListRowPy}
            >
              <Group
                gap={cmpHomeDashboardOrderDateOrderGap}
                alignItems="center"
                flexWrap="wrap"
                width="100%"
              >
                <Box {...cmpHomeDashboardOrderMetaBold}>
                  {formatOrderDate(order.checkoutDate) ?? "—"}
                </Box>
                <InternalTextLink href={orderUrl} {...cmpHomeDashboardOrderMetaLink}>
                  {order.id}
                </InternalTextLink>
              </Group>

              <Group
                width="100%"
                alignItems="center"
                flexWrap="wrap"
                gap={cmpHomeDashboardSectionRowGap}
                rowGap={cmpHomeDashboardSectionRowGap}
              >
                <Box flex="1 1 auto" minWidth={0} {...cmpHomeDashboardListSecondary}>
                  {order.userFullName || "—"}
                </Box>
                <Group
                  flex="0 1 auto"
                  minWidth={0}
                  maxWidth="100%"
                  ml="auto"
                  alignSelf="center"
                  alignItems="center"
                  justifyContent="space-between"
                  gap={8}
                >
                  {statusBadges}
                  {orderLinkButton}
                </Group>
              </Group>
            </Stack>
            {index < orders.length - 1 ? <Divider /> : null}
          </Fragment>
        );
      })}
    </>
  );
}
